import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { fetchPayoutQueue, fetchSettlementPreview, createSettlement, fetchSettlement } from "@admin/services/adminApi";
import { PageHeader } from "@admin/components/Primitives";
import { SettlementConfirmDialog, SettlementDetailDialog } from "@admin/components/payouts/SettlementDialogs";
import { PayoutQueueView } from "@admin/components/payouts/PayoutQueueView";
import { SettlementHistoryView } from "@admin/components/payouts/SettlementHistoryView";

const TABS = [["queue", "Weekly Queue"], ["history", "Settlements History"]];

function Tabs({ view, onChange }) {
  return (
    <div className="inline-flex rounded-full bg-slate-100 p-1 mb-6" role="tablist" data-testid="payouts-tabs">
      {TABS.map(([k, label]) => (
        <button key={k} type="button" role="tab" aria-selected={view === k} onClick={() => onChange(k)}
          className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${view === k ? "bg-[#0A2540] text-white shadow" : "text-slate-600 hover:text-[#0A2540]"}`}
          data-testid={`payouts-tab-${k}`}>{label}</button>
      ))}
    </div>
  );
}

export default function AdminPayouts() {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "history" ? "history" : "queue";
  const [q, setQ] = useState(null);
  const [preview, setPreview] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailMode, setDetailMode] = useState("queue");
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(null);

  const load = useCallback(() => fetchPayoutQueue().then(setQ).catch((e) => toast.error(e?.response?.data?.detail || "Could not load payout queue")), []);
  useEffect(() => { if (view === "queue") load(); }, [load, view]);

  const setView = (v) => {
    const next = new URLSearchParams(params);
    if (v === "history") next.set("view", "history"); else next.delete("view");
    setParams(next, { replace: false });
  };

  const openPreview = (row) => {
    setPreviewing(row.lawyer_id);
    fetchSettlementPreview(row.lawyer_id).then(setPreview)
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load preview"))
      .finally(() => setPreviewing(null));
  };
  const openDetail = (payoutId, mode = "queue") => {
    setDetailMode(mode);
    return fetchSettlement(payoutId).then(setDetail).catch((e) => toast.error(e?.response?.data?.detail || "Could not load settlement"));
  };

  const confirm = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const p = await createSettlement(preview.lawyer_id);
      toast.success(`Settlement ${p.payout_reference || p.payout_id} created — pending bank transfer`);
      setPreview(null);
      await load();
      openDetail(p.payout_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Settlement could not be created");
      setPreview(null);
      load();
    } finally {
      setBusy(false);
    }
  };

  const subtitle = view === "history"
    ? "Read-only reconciliation and audit view of every settlement ever created — pending, paid and failed."
    : "Eligible lawyer earnings for this week's manual bank-transfer settlement. Creating a settlement locks earnings; it is not a payment.";

  return (
    <>
      <PageHeader testid="admin-payouts-header" title="Weekly Payouts" subtitle={subtitle} />
      <Tabs view={view} onChange={setView} />

      {view === "history" ? (
        <SettlementHistoryView onOpen={(pid) => openDetail(pid, "history")} />
      ) : !q ? (
        <div className="text-slate-500" data-testid="payouts-loading">Loading…</div>
      ) : (
        <PayoutQueueView q={q} previewing={previewing} onPreview={openPreview} onViewDetail={(pid) => openDetail(pid, "queue")} />
      )}

      <SettlementConfirmDialog preview={preview} busy={busy} onCancel={() => setPreview(null)} onConfirm={confirm} />
      <SettlementDetailDialog settlement={detail} readOnly={detailMode === "history"} onClose={() => setDetail(null)}
        onChanged={(p) => {
          // Show the action's fresh payload immediately (no stale flash), then refetch.
          if (p) setDetail((d) => ({ ...(d || {}), ...p }));
          load();
          openDetail((p || detail).payout_id, detailMode);
        }} />
    </>
  );
}
